import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const USER_MGMT_ENDPOINT = `${API_BASE_URL}/user-management`;

export interface CompanyUser {
  id: string;
  displayName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  isSuperAdmin: boolean;
  roleName: string;
  userRoleId: number;
  lastActiveAt?: string;
  createdAt: string;
  isActive: boolean;
  isInvitationPending: boolean;
  invitedAt?: string;
  invitationAcceptedAt?: string;
  invitationId?: string;
}

export interface CompanyUsersResponse {
  users: CompanyUser[];
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

export interface InviteUserRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  roleName: string;
  invitationMessage?: string;
}

export interface UserInvitation {
  invitationId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roleName: string;
  invitedAt: string;
  expiresAt?: string;
  invitationToken: string;
  isAccepted: boolean;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  pendingInvitations: number;
  adminUsers: number;
  managerUsers: number;
  standardUsers: number;
}

/**
 * Helper function to acquire Auth0 access token.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for UserManagementService:',
      error
    );
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for UserManagementService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Failed to acquire access token for UserManagementService'
        );
      }
      return token;
    }
    throw new Error(`Token acquisition failed: ${error.message}`);
  }
};

/**
 * Helper function to make authenticated API requests.
 */
const makeAuthenticatedRequest = async (
  url: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  options: RequestInit = {},
  additionalHeaders?: Record<string, string>
): Promise<Response> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...additionalHeaders,
      ...options.headers
    }
  });

  if (!response.ok) {
    throw new Error(
      `API request failed: ${response.status} ${response.statusText}`
    );
  }

  return response;
};

export class UserManagementService {
  /**
   * Get all users for a company
   */
  static async getCompanyUsers(
    companyId: string,
    search?: string,
    roles?: string[],
    isActive?: boolean,
    sortBy?: string,
    sortDescending = false,
    page = 1,
    pageSize = 50,
    userId?: string,
    getAccessTokenSilently?: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup?: GetAccessTokenWithPopupType
  ): Promise<CompanyUsersResponse> {
    if (!getAccessTokenSilently || !getAccessTokenWithPopup) {
      throw new Error('Authentication functions not provided');
    }

    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roles?.length) params.append('roles', roles.join(','));
      if (isActive !== undefined)
        params.append('isActive', isActive.toString());
      if (sortBy) params.append('sortBy', sortBy);
      params.append('sortDescending', sortDescending.toString());
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());

      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/users?${params.toString()}`;
      const response = await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {},
        userId ? { 'X-User-Id': userId } : undefined
      );

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch company users:', error);
      throw error;
    }
  }

  /**
   * Invite a new user to the company
   */
  static async inviteUser(
    companyId: string,
    invitation: InviteUserRequest,
    userId: string,
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ): Promise<UserInvitation> {
    try {
      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/invite`;
      const response = await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {
          method: 'POST',
          body: JSON.stringify(invitation),
          headers: {
            'X-User-Id': userId
          }
        }
      );

      return await response.json();
    } catch (error) {
      console.error('Failed to invite user:', error);
      throw error;
    }
  }

  /**
   * Update a user's role
   */
  static async updateUserRole(
    companyId: string,
    userId: string,
    newRoleName: string,
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ): Promise<CompanyUser> {
    try {
      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/users/${userId}/role`;
      const response = await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {
          method: 'PUT',
          body: JSON.stringify({ newRoleName })
        }
      );

      return await response.json();
    } catch (error) {
      console.error('Failed to update user role:', error);
      throw error;
    }
  }

  /**
   * Deactivate a user
   */
  static async deactivateUser(
    companyId: string,
    userId: string,
    reason?: string,
    getAccessTokenSilently?: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup?: GetAccessTokenWithPopupType
  ): Promise<void> {
    if (!getAccessTokenSilently || !getAccessTokenWithPopup) {
      throw new Error('Authentication functions not provided');
    }

    try {
      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/users/${userId}/deactivate`;
      await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {
          method: 'POST',
          body: JSON.stringify({ reason })
        }
      );
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      throw error;
    }
  }

  /**
   * Get user statistics for a company
   */
  static async getUserStats(
    companyId: string,
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ): Promise<UserStats> {
    try {
      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/stats`;
      const response = await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      throw error;
    }
  }

  /**
   * Revoke a pending user invitation
   */
  static async revokeInvitation(
    invitationId: string,
    companyId: string,
    userId: string,
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ): Promise<void> {
    try {
      const url = `${API_BASE_URL}/invitations/${invitationId}?companyId=${companyId}&userId=${userId}`;
      await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {
          method: 'DELETE'
        }
      );
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      throw error;
    }
  }

  /**
   * Get all pending invitations for a company
   */
  static async getCompanyInvitations(
    companyId: string,
    userId: string,
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ): Promise<UserInvitation[]> {
    try {
      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/invitations`;
      const response = await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {},
        { 'X-User-Id': userId }
      );

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch company invitations:', error);
      throw error;
    }
  }

  /**
   * Resend an invitation email
   */
  static async resendInvitation(
    companyId: string,
    invitationId: string,
    userId: string,
    getAccessTokenSilently: GetAccessTokenSilentlyType,
    getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ): Promise<UserInvitation> {
    try {
      const url = `${USER_MGMT_ENDPOINT}/company/${companyId}/invitations/${invitationId}/resend`;
      const response = await makeAuthenticatedRequest(
        url,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        {
          method: 'POST'
        },
        { 'X-User-Id': userId }
      );

      return await response.json();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      throw error;
    }
  }
}
